import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, useAuth } from "@/context/AuthContext";
import PageHead from "@/components/PageHead";
import { ArrowLeft, Download, RefreshCcw, Mail } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function AdminSubscribers() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setError(null);
    setRefreshing(true);
    try {
      const { data } = await adminApi.get("/newsletter/admin/subscribers");
      setSubs(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      setError("Could not load subscribers.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmed = (subs || []).filter((s) => s.confirmed).length;
  const pending = (subs || []).filter((s) => !s.confirmed).length;

  const onExportCsv = () => {
    if (!subs) return;
    const rows = [["email", "confirmed", "created_at", "confirmed_at", "source"]];
    for (const s of subs) {
      rows.push([
        s.email,
        s.confirmed ? "yes" : "no",
        s.created_at || "",
        s.confirmed_at || "",
        s.source || "",
      ]);
    }
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `redkey-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-subscribers-page">
      <PageHead
        title="Subscribers — Redkey Admin"
        description="Manage newsletter subscribers."
      />

      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-20 pb-8">
        <Link
          to="/admin/journal"
          className="eyebrow link-underline-grow inline-flex items-center gap-2"
          data-testid="admin-subscribers-back"
        >
          <ArrowLeft size={12} aria-hidden />
          Back to blog manager
        </Link>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">§ Admin · Newsletter</p>
            <h1 className="font-display text-4xl md:text-6xl mt-4">
              <em className="not-italic text-brand-red">Subscribers.</em>
            </h1>
            <p className="mt-4 font-body text-ink-soft">
              {subs === null
                ? "Loading…"
                : `${confirmed} confirmed · ${pending} pending confirmation`}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              data-testid="admin-subscribers-refresh"
              className="inline-flex items-center gap-2 border hairline-strong px-5 py-3 hover:border-ink font-ui disabled:opacity-60"
            >
              <RefreshCcw size={14} aria-hidden />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              onClick={onExportCsv}
              disabled={!subs || subs.length === 0}
              data-testid="admin-subscribers-export"
              className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-3 hover:bg-electric transition-colors disabled:opacity-60 font-ui"
            >
              <Download size={14} aria-hidden />
              Export CSV
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1360px] px-6 md:px-10 border-t hairline pt-8 pb-24">
        {error && (
          <div
            className="border hairline-strong bg-paper-alt p-6 mb-8"
            data-testid="admin-subscribers-error"
          >
            <p className="font-body text-ink">{error}</p>
          </div>
        )}

        {subs === null && !error && (
          <p className="font-body text-ink-soft">Loading subscribers…</p>
        )}

        {subs !== null && subs.length === 0 && (
          <div
            className="border hairline-strong bg-paper-alt p-8"
            data-testid="admin-subscribers-empty"
          >
            <div className="flex items-start gap-4">
              <div className="mt-1 h-9 w-9 rounded-full bg-paper flex items-center justify-center border hairline-strong">
                <Mail size={16} aria-hidden />
              </div>
              <div>
                <p className="eyebrow">§ Empty list</p>
                <h2 className="font-display text-3xl mt-3">
                  No subscribers yet.
                </h2>
                <p className="mt-3 font-body text-ink-soft max-w-prose">
                  The subscribe form is live on the blog. When someone
                  confirms, they&rsquo;ll appear here.
                </p>
              </div>
            </div>
          </div>
        )}

        {subs !== null && subs.length > 0 && (
          <ul
            className="divide-y divide-[hsl(var(--line-strong))] border-y border-[hsl(var(--line-strong))]"
            data-testid="admin-subscribers-list"
          >
            {subs.map((s) => (
              <li
                key={s.id}
                data-testid={`admin-subscriber-${s.id}`}
                className="py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
              >
                <div className="md:col-span-5 font-body text-ink break-all">
                  {s.email}
                </div>
                <div className="md:col-span-2">
                  <span
                    className={`inline-block font-ui text-[10px] tracking-widest uppercase px-2 py-0.5 border ${
                      s.confirmed
                        ? "text-electric-ink border-electric-ink"
                        : "text-label border-[hsl(var(--line-strong))]"
                    }`}
                  >
                    {s.confirmed ? "Confirmed" : "Pending"}
                  </span>
                </div>
                <div className="md:col-span-3 font-ui text-xs text-label">
                  Joined {formatDate(s.created_at)}
                </div>
                <div className="md:col-span-2 font-ui text-xs text-label md:text-right">
                  {s.confirmed_at ? `Confirmed ${formatDate(s.confirmed_at)}` : "—"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
